enum DiscountType {
  PERCENT = 'percent',
  FIXED = 'fixed',
}

interface LineItemWorkOrder {
  workOrderLineNote?: string;
  lsWorkOrderId?: string;
  workOrderType?: string;
}

interface SerializedItem {
  lsSerializedID: string;
  serialNumber: string;
}

export default class LineItem {
  lsSaleLineId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
  subtotal: number;
  total: number;
  isTaxable: boolean;
  lineItemDiscount: LineItemDiscount;
  lineItemWorkOrder: LineItemWorkOrder;
  serializedItems: SerializedItem[];
  saleLineNote?: string;

  constructor(saleLine: SaleLine) {
    this.lsSaleLineId = `${saleLine.saleLineID}`;
    this.productName = this.parseProductName(saleLine);
    this.quantity = Number(saleLine.unitQuantity) ?? 0;
    this.unitPrice = Number(saleLine.unitPrice) ?? 0;
    this.unitCost = Number(saleLine.avgCost) ?? 0;
    this.total = Number(saleLine.calcTotal) ?? 0;
    this.subtotal = Number(saleLine.calcSubtotal) ?? 0;
    this.isTaxable = saleLine.tax === 'true';
    this.lineItemDiscount = new LineItemDiscount(saleLine);
    this.lineItemWorkOrder = this.parseWorkOrder(saleLine);
    this.serializedItems = this.parseSerialized(saleLine);
    this.saleLineNote = this.parseSaleLineNote(saleLine);
  }

  private parseSaleLineNote(saleLine: SaleLine): string | undefined {
    if (saleLine?.isWorkorder === 'true') {
      return '';
    }
    return saleLine.Note?.note || '';
  }
  private parseProductName(saleLine: SaleLine): string {
    return saleLine.Item?.description ?? saleLine.Note?.note ?? '';
  }

  private parseWorkOrder(saleLine: SaleLine): LineItemWorkOrder {
    if (saleLine.isWorkorder !== 'true') {
      return {
        workOrderLineNote: '',
        lsWorkOrderId: '',
        workOrderType: '',
      };
    }

    return {
      workOrderLineNote: this.getWorkOrderLineNote(saleLine),
      lsWorkOrderId: saleLine.SaleLineWorkOrder?.workOrderId || '',
      workOrderType: saleLine.SaleLineWorkOrder?.workOrderType || '',
    };
  }

  private getWorkOrderLineNote(saleLine: SaleLine): string {
    return saleLine.Item?.description && saleLine.Note?.note ? saleLine.Note.note : '';
  }

  private parseSerialized(saleLine: SaleLine): SerializedItem[] {
    if (saleLine.Item?.serialized !== 'true' || saleLine.Item?.itemType !== 'serialized') {
      return [];
    }

    const serializedData = saleLine.Serialized?.Serialized;
    if (!serializedData) {
      return [];
    }

    // Normalize to array regardless of whether it's a single object or array
    const serializedArray = Array.isArray(serializedData) ? serializedData : [serializedData];

    return serializedArray.map((item) => ({
      lsSerializedID: item?.serializedID,
      serialNumber: item?.serial,
    }));
  }
}
class LineItemDiscount {
  unitDiscountAmount: number;
  totalDiscountAmount: number;
  discountPercent: number;
  discountType: string;

  constructor(saleLine: SaleLine) {
    this.unitDiscountAmount = this.getUnitDiscount(saleLine);
    this.totalDiscountAmount = Number(saleLine?.calcLineDiscount) || 0;
    this.discountPercent = Number(saleLine?.discountPercent) || 0;
    this.discountType = this.parseDiscountType(saleLine);
  }

  private getUnitDiscount(saleLine: SaleLine): number {
    if (saleLine?.unitQuantity === '0') {
      return 0;
    }
    return Number(saleLine?.calcLineDiscount) / Number(saleLine?.unitQuantity);
  }

  private parseDiscountType(saleLine: SaleLine): string {
    if (saleLine?.Discount) {
      return Number(saleLine?.Discount?.discountAmount) === 0
        ? DiscountType.PERCENT
        : DiscountType.FIXED;
    }
    return '';
  }
}
